// Enhanced goal calculations with proper NaN/undefined handling

// Goals Configuration System for Bulletproof Tracking
export const GOALS_CONFIG = {
  // Revenue Goals - More realistic targets
  revenue: {
    monthly: {
      total: 500, // $500 monthly target
      oneTime: 200, // $200 from one-time purchases (40%)
      subscription: 300, // $300 from subscriptions (60%)
      growthRate: 12, // 12% monthly growth
    },
    quarterly: {
      total: 1500,
      oneTime: 600,
      subscription: 900,
      growthRate: 15,
    },
    yearly: {
      total: 6000,
      oneTime: 2400,
      subscription: 3600,
      growthRate: 20,
    },
  },

  // User Goals - More realistic targets
  users: {
    monthly: {
      total: 100, // 100 new users per month
      paid: 20, // 20% conversion to paid
      pro: 6, // 30% of paid users become pro subscribers
      retention: 85, // 85% retention rate
    },
    weekly: {
      new: 25, // 25 new users per week
      engaged: 15, // 60% engagement rate
    },
  },

  // Conversion Goals
  conversion: {
    paywall: {
      standard: 20, // 20% conversion from paywall to standard purchase
      pro: 12, // 12% conversion from paywall to pro subscription
      overall: 32, // Total conversion rate
    },
    funnel: {
      newToEngaged: 70, // 70% of new users become engaged
      engagedToStarted: 50, // 50% of engaged users start resume
      startedToSaved: 80, // 80% of started resumes get saved
      savedToPaid: 25, // 25% of saved resumes convert to paid
    },
  },

  // AI Feature Goals
  ai: {
    adoption: {
      autofill: 60, // 60% of users use AI autofill
      optimize: 40, // 40% of users use AI optimize
      suggestions: 80, // 80% of users view AI suggestions
      acceptance: 70, // 70% acceptance rate of AI suggestions
    },
  },

  // Feedback Goals
  feedback: {
    satisfaction: 85, // 85% satisfaction rate
    responseRate: 15, // 15% of users provide feedback
    positiveSentiment: 80, // 80% positive sentiment
  },
};

// Get custom goals from localStorage or fall back to defaults
const getCustomGoals = () => {
  try {
    const saved = localStorage.getItem("goalsConfig");
    if (saved) {
      const customGoals = JSON.parse(saved);
      // Merge with defaults to ensure all required fields exist
      return mergeWithDefaults(customGoals, GOALS_CONFIG);
    }
  } catch (error) {
    console.warn("Error loading custom goals from localStorage:", error);
  }
  return GOALS_CONFIG;
};

// Merge custom goals with defaults to ensure all fields exist
const mergeWithDefaults = (custom, defaults) => {
  const merged = { ...defaults };

  Object.keys(custom).forEach((category) => {
    if (merged[category]) {
      merged[category] = { ...merged[category] };

      Object.keys(custom[category]).forEach((subcategory) => {
        if (merged[category][subcategory]) {
          merged[category][subcategory] = {
            ...merged[category][subcategory],
            ...custom[category][subcategory],
          };
        } else {
          merged[category][subcategory] = custom[category][subcategory];
        }
      });
    } else {
      merged[category] = custom[category];
    }
  });

  return merged;
};

// Sanitize any metric to prevent NaN/undefined
export const sanitizeMetric = (value, fallback = 0) => {
  if (
    value === null ||
    value === undefined ||
    isNaN(value) ||
    !isFinite(value)
  ) {
    return fallback;
  }
  return Number(value);
};

// Format metric for display
export const formatMetric = (value, type = "number") => {
  const sanitized = sanitizeMetric(value);

  switch (type) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(sanitized);
    case "percentage":
      return `${sanitized.toFixed(1)}%`;
    case "number":
    default:
      return sanitized.toLocaleString();
  }
};

// Get time-based goals with proper validation
export const getTimeBasedGoals = (timeRange) => {
  const days = sanitizeMetric(timeRange, 30);
  const months = days / 30;
  const weeks = days / 7;

  // Get custom goals or fall back to defaults
  const goals = getCustomGoals();

  // Determine which goal set to use with fallbacks
  let goalSet = goals.revenue?.monthly || {
    total: 5000,
    oneTime: 3000,
    subscription: 2000,
    growthRate: 0.1,
  };
  let userGoalSet = goals.users?.monthly || {
    total: 1000,
    paid: 200,
    pro: 100,
    new: 50,
    engaged: 300,
  };

  // Adjust goals based on time range with realistic scaling
  let timeMultiplier;
  if (days <= 7) {
    timeMultiplier = days / 30; // Weekly goals
  } else if (days <= 30) {
    timeMultiplier = days / 30; // Monthly goals
  } else if (days <= 90) {
    timeMultiplier = days / 30; // Quarterly goals
  } else {
    timeMultiplier = days / 365; // Yearly goals
  }

  return {
    revenue: {
      total: Math.round(sanitizeMetric(goalSet.total) * timeMultiplier),
      oneTime: Math.round(sanitizeMetric(goalSet.oneTime) * timeMultiplier),
      subscription: Math.round(
        sanitizeMetric(goalSet.subscription) * timeMultiplier
      ),
      growthRate: sanitizeMetric(goalSet.growthRate, 0.1),
    },
    users: {
      total: Math.round(sanitizeMetric(userGoalSet.total) * timeMultiplier),
      paid: Math.round(sanitizeMetric(userGoalSet.paid) * timeMultiplier),
      pro: Math.round(sanitizeMetric(userGoalSet.pro) * timeMultiplier),
      new: Math.round(sanitizeMetric(userGoalSet.new) * weeks),
      engaged: Math.round(sanitizeMetric(userGoalSet.engaged) * weeks),
    },
    conversion: {
      paywall: {
        standard: sanitizeMetric(goals.conversion?.paywall?.standard, 2.5),
        pro: sanitizeMetric(goals.conversion?.paywall?.pro, 1.5),
      },
    },
    ai: {
      adoption: {
        autofill: sanitizeMetric(goals.ai?.adoption?.autofill, 15),
        optimize: sanitizeMetric(goals.ai?.adoption?.optimize, 8),
      },
    },
    feedback: {
      satisfaction: sanitizeMetric(goals.feedback?.satisfaction, 85),
    },
  };
};

// Calculate percentage change with proper handling of zero values
export const calculatePercentageChange = (current, previous, goal = null) => {
  const sanitizedCurrent = sanitizeMetric(current);
  const sanitizedPrevious = sanitizeMetric(previous);
  const sanitizedGoal = sanitizeMetric(goal);

  // If we have no previous data, compare against goal
  if (
    sanitizedPrevious === 0 ||
    sanitizedPrevious === null ||
    sanitizedPrevious === undefined
  ) {
    if (sanitizedGoal && sanitizedGoal > 0) {
      const change = ((sanitizedCurrent - sanitizedGoal) / sanitizedGoal) * 100;
      return isFinite(change) ? change : 0;
    }
    return 0; // No meaningful comparison possible
  }

  // Calculate percentage change
  const change =
    ((sanitizedCurrent - sanitizedPrevious) / sanitizedPrevious) * 100;

  // If change is infinite or NaN, return 0
  if (!isFinite(change)) {
    return 0;
  }

  return change;
};

// Calculate progress towards goal
export const calculateProgress = (current, goal) => {
  const sanitizedCurrent = sanitizeMetric(current);
  const sanitizedGoal = sanitizeMetric(goal);

  if (sanitizedGoal <= 0) return 0;

  const progress = (sanitizedCurrent / sanitizedGoal) * 100;
  return Math.min(Math.max(progress, 0), 100); // Clamp between 0 and 100
};

// Get goal-based comparison for metrics
export const getGoalComparison = (current, previous, goal, metricType) => {
  const sanitizedCurrent = sanitizeMetric(current);
  const sanitizedPrevious = sanitizeMetric(previous);
  const sanitizedGoal = sanitizeMetric(goal);

  const change = calculatePercentageChange(
    sanitizedCurrent,
    sanitizedPrevious,
    sanitizedGoal
  );

  return {
    current: sanitizedCurrent,
    previous: sanitizedPrevious,
    goal: sanitizedGoal,
    change: isFinite(change) ? change : 0,
    progress: calculateProgress(sanitizedCurrent, sanitizedGoal),
    isPositive: change >= 0,
    isOnTrack: sanitizedCurrent >= sanitizedGoal * 0.8, // 80% of goal is considered on track
    metricType,
  };
};

// Calculate conversion rate with proper validation
export const calculateConversionRate = (
  conversions,
  impressions,
  goal = null
) => {
  const sanitizedConversions = sanitizeMetric(conversions);
  const sanitizedImpressions = sanitizeMetric(impressions);
  const sanitizedGoal = sanitizeMetric(goal);

  if (sanitizedImpressions <= 0) {
    return {
      rate: 0,
      change: 0,
      progress: 0,
      isValid: false,
    };
  }

  const rate = (sanitizedConversions / sanitizedImpressions) * 100;
  const validRate = isFinite(rate) ? Math.min(rate, 100) : 0; // Cap at 100%

  return {
    rate: validRate,
    change: sanitizedGoal
      ? calculatePercentageChange(validRate, sanitizedGoal)
      : 0,
    progress: sanitizedGoal ? calculateProgress(validRate, sanitizedGoal) : 0,
    isValid: true,
  };
};

// Enhanced percentage change calculation
export const calculatePercentageChangeEnhanced = (current, previous) => {
  const sanitizedCurrent = sanitizeMetric(current);
  const sanitizedPrevious = sanitizeMetric(previous);

  if (sanitizedPrevious <= 0) return 0;

  const change =
    ((sanitizedCurrent - sanitizedPrevious) / sanitizedPrevious) * 100;
  return isFinite(change) ? change : 0;
};

// Calculate funnel conversion rates
export const calculateFunnelConversion = (currentStage, previousStage) => {
  const sanitizedCurrent = sanitizeMetric(currentStage);
  const sanitizedPrevious = sanitizeMetric(previousStage);

  if (sanitizedPrevious <= 0) return 0;

  const conversion = (sanitizedCurrent / sanitizedPrevious) * 100;
  return isFinite(conversion) ? Math.min(conversion, 100) : 0;
};

// Validate and format time series data
export const validateTimeSeriesData = (data) => {
  if (!Array.isArray(data)) return [];

  return data
    .map((item) => ({
      date: item.date || new Date().toISOString(),
      count: sanitizeMetric(item.count),
      value: sanitizeMetric(item.value),
      revenue: sanitizeMetric(item.revenue),
      downloads: sanitizeMetric(item.downloads),
      subscriptions: sanitizeMetric(item.subscriptions),
    }))
    .filter(
      (item) =>
        item.date && (item.count > 0 || item.value > 0 || item.revenue > 0)
    );
};

// Calculate averages with validation
export const calculateAverage = (values) => {
  if (!Array.isArray(values) || values.length === 0) return 0;

  const validValues = values.map((v) => sanitizeMetric(v)).filter((v) => v > 0);
  if (validValues.length === 0) return 0;

  const sum = validValues.reduce((acc, val) => acc + val, 0);
  const average = sum / validValues.length;

  return isFinite(average) ? average : 0;
};

// Calculate growth rate with validation
export const calculateGrowthRate = (current, previous, timePeriod = 1) => {
  const sanitizedCurrent = sanitizeMetric(current);
  const sanitizedPrevious = sanitizeMetric(previous);
  const sanitizedTimePeriod = sanitizeMetric(timePeriod, 1);

  if (sanitizedPrevious <= 0 || sanitizedTimePeriod <= 0) return 0;

  const growthRate =
    (sanitizedCurrent - sanitizedPrevious) /
    sanitizedPrevious /
    sanitizedTimePeriod;
  return isFinite(growthRate) ? growthRate : 0;
};
